import { OrganizationIdSchema } from "@elaso/shared/schemas/organization";
import type { SocketScope } from "./socket-owner";

/** The clients a sign-in returns somebody to, each with the pages inside it. */
const CLIENTS = ["/app", "/operations", "/organization", "/instance"] as const;

type Client = (typeof CLIENTS)[number];

/**
 * A page of one of this ELASO Instance's clients, its front page included, kept
 * whole so that sign-in returns somebody to the page they were heading to.
 */
type ClientPage = (Client | `${Client}/${string}`) & {
	readonly __clientPage: unique symbol;
};

/** An invite somebody opened, kept whole, because its code is what accepting it needs. */
type Invite = `/invite/${string}` & { readonly __invite: unique symbol };

const INVITE = /^\/invite\/[^/]+$/;

/**
 * The Organisation a window of the Organisation Admin Client is open on, which
 * is the window
 * (docs/adr/39-two-admin-clients-the-elaso-instance-admin-client-and-the-organisation-admin-client.md).
 */
const ORGANIZATION_WINDOW = /^\/organization\/([^/]+)(?:\/|$)/;

/** Where a sign-in sends somebody on to: a page of one of this ELASO Instance's clients, or the invite they opened. */
export type Destination = ClientPage | Invite;

/** The client a path is in, when it is in one. */
export const clientOf = (pathname: string): Client | undefined =>
	CLIENTS.find(
		(client) => pathname === client || pathname.startsWith(`${client}/`)
	);

/**
 * A stand-in origin to resolve against. A value that resolves anywhere else
 * named a host of its own, which is the open redirect this exists to refuse.
 */
const HERE = "https://destination.invalid";

/**
 * `?redirect=` as every sign-in page accepts it: a page of one of this ELASO
 * Instance's clients, or the invite it names, and nothing else.
 *
 * `?redirect=` is in a URL a stranger can hand somebody, so a value that is not
 * one of ours is a stale link or an attempt to send a freshly signed-in user
 * somewhere of the sender's choosing, and both get `undefined`. Resolving it as
 * a URL is what makes that safe: `//host`, `/\\host`, a tab inside the slashes,
 * a scheme and `..` are all decided by the same parser the browser uses, and
 * whatever escapes this origin is refused. The page is kept, so a deep link or
 * a link in an email returns somebody where it pointed; the query and the
 * fragment are dropped, because no page of a client reads either. A window
 * address naming no Organisation returns them to the client's front page.
 */
export const parseDestination = (raw: unknown): Destination | undefined => {
	if (typeof raw !== "string" || !raw.startsWith("/")) return;
	let url: URL;
	try {
		url = new URL(raw, HERE);
	} catch {
		return;
	}
	if (url.origin !== HERE) return;
	const { pathname } = url;
	if (INVITE.test(pathname)) return pathname as Invite;
	const client = clientOf(pathname);
	if (!client) return;
	const organizationId = ORGANIZATION_WINDOW.exec(pathname)?.[1];
	if (
		organizationId !== undefined &&
		!OrganizationIdSchema.safeParse(organizationId).success
	) {
		return client as ClientPage;
	}
	return pathname as ClientPage;
};

/** Which client's data the socket loads for a destination. No destination is the Learner Client. */
export const scopeOfDestination = (
	destination: Destination | undefined
): SocketScope => {
	const client = destination && clientOf(destination);
	if (client === "/operations") return "operations";
	if (client === "/organization") return "organization";
	return "app";
};

/**
 * The page the user is on, or the invite they are on, as a destination: read
 * from the path on the web, and from the route inside the hash on the desktop
 * build.
 */
export const currentDestination = (): Destination | undefined => {
	const { pathname, hash } = location;
	return parseDestination(hash.startsWith("#/") ? hash.slice(1) : pathname);
};

/**
 * The search for the sign-in page that a signed-out user is sent to from any
 * client, carrying the page they were on so sign-in returns them to it.
 */
export const signInSearch = (): { redirect?: Destination } => {
	const redirect = currentDestination();
	return redirect ? { redirect } : {};
};
